import * as RadixPopover from '@radix-ui/react-popover';
import {
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/state/Skeleton';
import { cn } from '@/lib/cn';

import { INPUT_CLASS } from './TextField';
import { stopPortalClick } from './portal-events';
import { usePortalTheme } from './use-portal-theme';

export interface SuggestFieldProps {
  /** The text in the field. Controlled: this component never owns the value. */
  value: string;
  /** The ordinary input change — spread `register(...)` and this comes with it. */
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * Esc ble trykket mens lista var åpen — for et felt der verdien MÅ komme fra katalogen.
   *
   * Standarden er å beholde det som er skrevet: i et fritekstfelt ER teksten verdien, og en
   * suggestion er et forslag og ikke en forpliktelse. Tidssonefeltet er det motsatte — «Stock» er
   * ingen sone — og der ble søketeksten stående igjen som om den var et svar (QA 20. september).
   * Kalleren setter teksten tilbake til den valgte verdien her.
   */
  onCancel?: () => void;
  /** A suggestion was chosen. The caller writes it to the form; the field is still free text. */
  onPick: (value: string) => void;
  /**
   * The panel is opening, or the text changed while it was open. The caller starts asking for
   * suggestions HERE and not on mount — an untouched field must not put a request on the wire.
   */
  onOpenChange?: (open: boolean) => void;
  items: readonly string[];
  loading?: boolean;
  name?: string;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
  ref?: Ref<HTMLInputElement>;
}

/**
 * A text field that suggests what the organization already says — and still takes anything.
 *
 * WHY THIS IS NOT A `<datalist>`. It is the same fault as `<select>` and `<input type="time">`:
 * the list is painted by the operating system, outside the page, where no rule of ours reaches
 * it — a white box with a system-blue row in the middle of a dark interface. DESIGN.md §13
 * settles it for every control of this kind ("Ingen native velgere. Noensinne."), and
 * `no-native-pickers.spec.ts` fails the moment one comes back. So the behaviour comes from Radix
 * and the surface is ours.
 *
 * WHY IT IS NOT A `Select` EITHER. Atle's reason for the endpoint is that "Live Nation" and
 * "Live Nation Norway" must not both end up in the register — not that the register may only
 * contain companies it already knows. The first contact at a new company has to be enterable, so
 * this is free text with a list beside it, never a closed vocabulary. Nothing here can refuse a
 * value; the panel only shows what is already said.
 *
 * FOCUS NEVER LEAVES THE INPUT. The options are not tab stops and are never focused: the active
 * one is pointed at with `aria-activedescendant`, which is what lets someone keep typing while
 * the list narrows under them. Arrows move, Enter picks, Escape closes and keeps what was typed.
 *
 * THE PANEL IS PORTALLED, SO IT CARRIES ITS COLOUR MODE. `usePortalTheme` reads the mode off the
 * input — the part of this control that is still inside the pane — and stamps the panel with it.
 * Without that, a panel opened from the dark half of `/design` comes back in the document's mode:
 * the bug Atle already reported once, and the reason `portal-theming.spec.ts` sweeps every file
 * in this directory rather than a list of the ones that have gone wrong so far.
 */
export function SuggestField({
  value,
  onChange,
  onBlur,
  onCancel,
  onPick,
  onOpenChange,
  items,
  loading = false,
  name,
  id,
  disabled = false,
  readOnly = false,
  className,
  ref,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'data-testid': testId,
}: SuggestFieldProps): ReactNode {
  const { t } = useTranslation();
  const portal = usePortalTheme();

  const listId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  /** -1 is "nothing pointed at", which is where every open and every keystroke starts. */
  const [active, setActive] = useState(-1);

  /**
   * Open, and tell the caller — which is the moment the suggestions are actually asked for. The
   * colour mode is captured here too, at open time rather than at mount, so a theme toggled with
   * the panel closed is still the mode the panel comes back in.
   */
  function setPanel(next: boolean): void {
    if (next && (disabled || readOnly)) return;
    if (next) portal.capture();
    setOpen(next);
    if (!next) setActive(-1);
    onOpenChange?.(next);
  }

  function pick(index: number): void {
    const chosen = items[index];
    if (chosen === undefined) return;
    onPick(chosen);
    setPanel(false);
    inputRef.current?.focus();
  }

  function move(step: number): void {
    if (items.length === 0) return;
    // Wraps at both ends: the list is short, and a list that stops dead makes you arrow all the
    // way back to reach the row above the one you started on.
    setActive((current) => (current + step + items.length) % items.length);
  }

  const showEmpty = !loading && items.length === 0 && value.trim().length > 0;
  const panelOpen = open && (loading || items.length > 0 || showEmpty);
  const activeId = active >= 0 && active < items.length ? `${listId}-${active}` : undefined;

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setPanel(true);
      else move(1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setPanel(true);
      else move(-1);
      return;
    }
    if (event.key === 'Enter' && panelOpen) {
      /*
       * ENTER MED NOE PEKT PÅ PLUKKER DET. ENTER MED INGENTING PEKT PÅ LUKKER PANELET OG GÅR
       * VIDERE (QA runde 10, P2 #6).
       *
       * ------------------------------------------------------------------------------------------
       * HER STO DET MOTSATTE, MED EN BEGRUNNELSE SOM VAR RIMELIG OG LIKEVEL FEIL:
       *
       *   «Enter with nothing pointed at closes the panel instead of submitting the form underneath
       *    it: the panel is covering the footer, and a save nobody could see coming is worse than
       *    one keystroke that did nothing but tidy up.»
       *
       * Frykten er reell, men prisen ble større. Rollefeltet i Management er det SISTE feltet i
       * skjemaet — man skriver navn, telefon, e-post, rolle — og et `preventDefault()` uansett
       * betyr at tasten aldri når skjemaet under. To QA-runder på rad meldte «Enter lagrer ikke i
       * Management», og begge ganger var det denne linja, ikke skjemaet.
       *
       * At Enter lagrer er husets regel siden runde 6. En kontroll som gjør unntak fra den i det
       * ene feltet folk faktisk slutter i, opphever regelen — man slutter å stole på den og tar
       * musa hver gang.
       *
       * «En lagring ingen så komme» er heller ikke det som skjer: den som trykker Enter i et skjema
       * ber om å lagre. Panelet er et forslag, ikke en dialog, og det lukkes i samme bevegelse.
       */
      if (active >= 0) {
        event.preventDefault();
        pick(active);
        return;
      }
      setPanel(false);
      return;
    }
    if (event.key === 'Escape' && open) {
      // Keeps what was typed — the panel is a suggestion, not a commitment. `stopPropagation`
      // because this control is used inside a dialog, and one Escape must close one layer.
      event.preventDefault();
      event.stopPropagation();
      setPanel(false);
      onCancel?.();
      return;
    }
    if (event.key === 'Tab' && open) setPanel(false);
  }

  return (
    <RadixPopover.Root open={panelOpen} onOpenChange={(next) => setPanel(next)}>
      <RadixPopover.Anchor asChild>
        <input
          id={id}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={panelOpen}
          aria-controls={panelOpen ? listId : undefined}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          // Never the browser's own remembered values: that list is drawn by the platform, over
          // the one we drew, and it is the very surface DESIGN.md §13 rules out.
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          readOnly={readOnly}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          data-testid={testId}
          value={value}
          ref={(node) => {
            inputRef.current = node;
            portal.anchorRef(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          onChange={(event) => {
            onChange(event);
            // Every keystroke re-aims the list, so nothing stays pointed at a row that has
            // just scrolled out from under it.
            setActive(-1);
            if (!open) setPanel(true);
          }}
          onFocus={() => setPanel(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={cn(INPUT_CLASS, className)}
        />
      </RadixPopover.Anchor>

      <RadixPopover.Portal>
        <RadixPopover.Content
          // Stamped with the colour mode of the pane the input lives in, because this panel is
          // portalled out of it. See `usePortalTheme`.
          {...portal.attributes}
          onClick={stopPortalClick}
          // To the DOM this panel is nowhere near the field, but focus never leaves the input, so
          // `Field` would keep its label floated anyway. The marker is here for the same reason
          // `TimeField` carries it: the field and its surface are one control.
          data-field-surface={id}
          data-testid="suggest-panel"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          // The list is read with the eyes and driven from the input. Moving focus into it would
          // take the caret out of the word being typed.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          // The input is OUTSIDE this panel, so every click in the field reads as a click
          // outside it — and the panel would close the moment somebody moved the caret. The
          // control is the input plus the panel; only the rest of the page is outside.
          onInteractOutside={(event) => {
            const target = event.target;
            if (target instanceof Node && inputRef.current?.contains(target) === true) {
              event.preventDefault();
            }
          }}
          className={cn(
            'glass-popover z-50 overflow-hidden',
            'text-default',
            'w-[var(--radix-popover-trigger-width)]',
            'max-h-[var(--radix-popover-content-available-height)]',
          )}
        >
          {loading ? (
            // A skeleton in the shape of three rows, never a spinner (DESIGN.md §11.1).
            <div className="flex flex-col gap-1 p-1" aria-hidden data-testid="suggest-loading">
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-4/5" />
              <Skeleton className="h-7 w-3/5" />
            </div>
          ) : showEmpty ? (
            <p className="max-w-64 px-2 py-2 text-2xs leading-snug text-muted">
              {t('suggest.empty')}
            </p>
          ) : (
            <div
              role="listbox"
              id={listId}
              aria-label={t('suggest.label')}
              className="flex max-h-64 flex-col overflow-y-auto p-1"
            >
              {items.map((item, index) => (
                <button
                  key={item}
                  type="button"
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  // Never a tab stop: the input keeps the focus and points here with
                  // aria-activedescendant, which is what lets someone keep typing while the list
                  // narrows. A real button — like `TimeField`'s options — so the pointer target
                  // is a control rather than a div wearing a role.
                  tabIndex={-1}
                  // Before the click, and before the blur it would otherwise cause: taking the
                  // focus out of the input would close the panel under the finger.
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => pick(index)}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    // The 44px floor (DESIGN.md §9) applies to a narrow panel row the way it does
                    // to a picker column: there is no width here to compensate with.
                    'flex h-11 w-full cursor-pointer items-center rounded-[var(--radius-menu-row)] px-2 text-start',
                    'text-sm text-default outline-none select-none',
                    // Pointed at is a tint AND a weight, so the row survives a washed-out screen.
                    index === active && 'bg-accent-subtle font-medium text-accent-text',
                  )}
                >
                  <span className="truncate">{item}</span>
                </button>
              ))}
            </div>
          )}
        </RadixPopover.Content>
      </RadixPopover.Portal>

      {/* The count, for someone who cannot see the panel open under the field. */}
      <span role="status" className="sr-only">
        {panelOpen && !loading && items.length > 0
          ? t('suggest.count', { count: items.length })
          : ''}
      </span>
    </RadixPopover.Root>
  );
}
